import React, { useEffect, useRef, useState } from 'react';
import { TIER_CONFIG } from '@/config/constants';
import { Badge } from '@/components/ui/Badge';

interface TierSelectorProps {
  selectedTier: 'standard' | 'seasonal';
  onTierChange: (tier: 'standard' | 'seasonal') => void;
  disabled?: boolean;
  // 'switch' (default): tabs preview, explicit "Switch Plan" button commits —
  // for Settings where switching has real consequences (grace period, spend
  // requirements). 'select': tab click commits immediately, no Current/Switch
  // UI — for Signup where the visitor is picking their initial plan.
  mode?: 'switch' | 'select';
}

type FreeTier = 'standard' | 'seasonal';

const FREE_TIER_META: Record<
  FreeTier,
  {
    label: string;
    badge?: string;
    budgetLine: string;
    descriptionLines: string[];
  }
> = {
  standard: {
    label: 'Standard',
    budgetLine: `Requires $${TIER_CONFIG.STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS}/month in ads spend per account`,
    descriptionLines: [
      'Best for running campaigns year-round.',
      `Spend $${TIER_CONFIG.STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS}/month after 1 month.`,
    ],
  },
  seasonal: {
    label: 'Seasonal',
    badge: 'Flexible',
    budgetLine: `Requires $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR}/year in ads spend per account`,
    descriptionLines: [
      'Perfect for seasonal advertisers.',
      `Spend $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR} in 4 months, $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR}/year onward.`,
    ],
  },
};

const FREE_TIER_FEATURES: string[] = [
  'Google Ads OR Meta Ads (pick one)',
  'AI-powered recommendations',
  'Automated budget allocation',
  'Maintain full control of your campaigns',
];

const PRO_TIER_FEATURES: string[] = [
  'All features in the Free plans',
  'No minimum ad spend requirement',
  'Google Ads AND Meta Ads',
  'Automated Omni-channel budget allocation',
  'Creative Studio for content development and brand standardization',
  'Maintain full control of your campaigns',
];

/**
 * Choose Your Plan surface. Free tiers (Standard + Seasonal) share a card
 * with a tab switcher. Two modes:
 *   - `mode="switch"` (default, Settings): tab click only previews the tier;
 *     an explicit "Switch Plan" button fires `onTierChange`, so the caller
 *     can gate the commit behind a confirm dialog without trapping a user
 *     who just wants to compare the two.
 *   - `mode="select"` (Signup): tab click (and ArrowLeft/ArrowRight) fires
 *     `onTierChange` immediately; the "Current/Switch Plan" affordance is
 *     hidden because the visitor is picking their initial plan, not
 *     changing an existing one.
 *
 * The Pro placeholder card to the right is display-only on this branch;
 * the Pro branch will activate it once checkout is wired.
 */
export const TierSelector: React.FC<TierSelectorProps> = ({
  selectedTier,
  onTierChange,
  disabled = false,
  mode = 'switch',
}) => {
  // Local "which tab is being viewed" state. Independent of the committed
  // `selectedTier` prop so the user can preview the other tier without
  // triggering the caller's onTierChange handler (which in Settings pops
  // a confirmation dialog).
  const [viewedTier, setViewedTier] = useState<FreeTier>(selectedTier);

  // When the caller commits a switch (or the current tier changes for any
  // other reason), snap the viewed tab back to the committed one so the
  // badge/button state reflects reality.
  useEffect(() => {
    setViewedTier(selectedTier);
  }, [selectedTier]);

  const activeMeta = FREE_TIER_META[viewedTier];
  const isViewingCurrent = viewedTier === selectedTier;

  // ARIA tab pattern: ArrowLeft / ArrowRight cycle between tabs, and only
  // the active tab is tabbable (`tabIndex={0}` on active, `-1` on inactive
  // — "roving tabindex"). Home/End jump to first/last. Focus follows the
  // selection so screen readers announce the new tab immediately.
  const TIER_ORDER: FreeTier[] = ['standard', 'seasonal'];
  const tabRefs = useRef<Record<FreeTier, HTMLButtonElement | null>>({
    standard: null,
    seasonal: null,
  });

  const handleTabKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    tier: FreeTier,
  ) => {
    const currentIdx = TIER_ORDER.indexOf(tier);
    let nextTier: FreeTier | null = null;
    if (e.key === 'ArrowRight') {
      nextTier = TIER_ORDER[(currentIdx + 1) % TIER_ORDER.length];
    } else if (e.key === 'ArrowLeft') {
      nextTier =
        TIER_ORDER[(currentIdx - 1 + TIER_ORDER.length) % TIER_ORDER.length];
    } else if (e.key === 'Home') {
      nextTier = TIER_ORDER[0];
    } else if (e.key === 'End') {
      nextTier = TIER_ORDER[TIER_ORDER.length - 1];
    }
    if (nextTier && nextTier !== tier) {
      e.preventDefault();
      setViewedTier(nextTier);
      if (mode === 'select' && !disabled) onTierChange(nextTier);
      // Focus the newly-activated tab so keyboard users track the
      // selection without a Tab press.
      tabRefs.current[nextTier]?.focus();
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <h3 className="text-2xl font-semibold text-gray-900 text-center mb-6">
        Choose Your Plan
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {/* Free card — tabbed */}
        <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
          <div
            role="tablist"
            aria-label="Free plan variant"
            className="flex border-b border-gray-200"
          >
            {(Object.keys(FREE_TIER_META) as FreeTier[]).map((tier) => {
              const isActive = viewedTier === tier;
              const meta = FREE_TIER_META[tier];
              return (
                <button
                  key={tier}
                  ref={(el) => {
                    tabRefs.current[tier] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`tier-panel-${tier}`}
                  id={`tier-tab-${tier}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => {
                    setViewedTier(tier);
                    if (mode === 'select' && !disabled) onTierChange(tier);
                  }}
                  onKeyDown={(e) => handleTabKeyDown(e, tier)}
                  className={[
                    'flex-1 py-3 px-4 text-sm font-medium text-center transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600 -mb-px'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  ].join(' ')}
                >
                  {meta.label}
                  {meta.badge && (
                    <span
                      className={[
                        'ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full',
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600',
                      ].join(' ')}
                    >
                      {meta.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            id={`tier-panel-${viewedTier}`}
            aria-labelledby={`tier-tab-${viewedTier}`}
            className="p-6 flex flex-col flex-1 gap-3"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-3xl font-bold text-flouri-darkGray">
                Free
              </span>
              {mode === 'switch' &&
                (isViewingCurrent ? (
                  <span
                    className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
                    aria-label="Current plan"
                  >
                    Current Plan
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onTierChange(viewedTier)}
                    className={[
                      'inline-flex items-center px-4 py-1.5 text-sm font-medium rounded-md',
                      'bg-blue-700 text-white shadow-sm',
                      'transition-colors duration-200 ease-out',
                      'hover:bg-blue-800',
                      'disabled:bg-gray-400 disabled:cursor-not-allowed',
                    ].join(' ')}
                  >
                    Switch Plan
                  </button>
                ))}
            </div>
            <p className="text-xs text-gray-500 -mt-1 min-h-9">
              {activeMeta.budgetLine}
              <br />
              <strong>100% goes toward advertising spend.</strong>
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              {activeMeta.descriptionLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < activeMeta.descriptionLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <ul className="mt-2 space-y-2">
              {FREE_TIER_FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="text-sm text-gray-600 flex items-start gap-2"
                >
                  <span className="text-green-500 font-bold leading-5">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Pro placeholder — not selectable on this branch */}
        <div
          className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col"
          aria-label="Pro plan (coming soon)"
        >
          <div className="py-3 px-4 border-b border-gray-200 flex items-center justify-center gap-2">
            <span className="text-sm font-medium text-gray-600">Professional</span>
            <Badge variant="warning">Coming Soon!</Badge>
          </div>

          <div className="p-6 flex flex-col flex-1 gap-3">
            <div className="flex items-baseline gap-1" aria-hidden="true">
              <span className="text-3xl font-bold text-flouri-darkGray">$</span>
              <span className="text-3xl font-bold text-flouri-darkGray blur-md select-none">
                39
              </span>
              <span className="text-sm text-gray-500 blur-md select-none">
                /month
              </span>
            </div>
            <span className="sr-only">Pricing coming soon</span>
            <p className="text-xs text-gray-500 -mt-1 min-h-9">
              <strong>No required minimum ads spend.</strong>
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Unlock omni-channel budget optimization across Google Ads and
              Meta Ads in one Flouri account.
            </p>
            <ul className="mt-2 space-y-2">
              {PRO_TIER_FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="text-sm text-gray-600 flex items-start gap-2"
                >
                  <span className="text-green-500 font-bold leading-5">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierSelector;
